/* PDP Redesign — Phase 1
 * Sticky add-to-cart bar: shown once the main Add to Cart button scrolls
 * out of view, hidden again once it's back in view. Uses IntersectionObserver,
 * following the same pattern already used elsewhere in this theme (see
 * assets/theme.js "cc-animate" IntersectionObserver usage).
 */
(function () {
  'use strict';

  function init() {
    var stickyBar = document.querySelector('[data-pdp-sticky-atc]');
    var mainAddToCart = document.querySelector('[data-pdp-main-add-to-cart]');

    if (!stickyBar || !mainAddToCart || !('IntersectionObserver' in window)) {
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          /* Only once the button has been scrolled past, not while it is still below the fold. */
          stickyBar.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
        });
      },
      { threshold: 0 }
    );

    observer.observe(mainAddToCart);
  }

  /* "Complete the order" add-ons: toggle add/remove using the same cart
   * endpoints the rest of the theme's ajax add-to-cart flow uses
   * (theme.routes.cart_add_url / theme.routes.cart_change_url — see
   * theme.applyAjaxToProductForm in assets/theme.js), rather than a new
   * cart API pattern.
   *
   * A row with several variants ([data-pdp-addon-varies]) never adds on the
   * first tap: the toggle opens a pill panel, a pill fills in the variant id
   * and the panel's own Add button does the cart call.
   */
  function initAddonToggles() {
    var rows = document.querySelectorAll('[data-pdp-addon]');
    if (!rows.length || typeof window.theme === 'undefined' || !window.theme.routes) {
      return;
    }

    function postCart(url, body) {
      return fetch(url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body
      }).then(function (response) {
        if (!response.ok) {
          throw new Error('cart request failed');
        }
        document.documentElement.dispatchEvent(
          new CustomEvent('theme:cartchanged', { bubbles: true, cancelable: false })
        );
      });
    }

    rows.forEach(function (row) {
      var form = row.querySelector('[data-pdp-addon-form]');
      var toggle = row.querySelector('[data-pdp-addon-toggle]');
      if (!form || !toggle) {
        return;
      }

      var idInput = form.querySelector('input[name="id"]');
      var varies = row.hasAttribute('data-pdp-addon-varies');
      var options = row.querySelector('[data-pdp-addon-options]');
      var pills = options ? options.querySelectorAll('[data-pdp-addon-pill]') : [];
      var confirm = options ? options.querySelector('[data-pdp-addon-confirm]') : null;
      var selectedLabel = options ? options.querySelector('[data-pdp-addon-selected]') : null;
      var price = row.querySelector('[data-pdp-addon-price]');
      var variantLabel = row.querySelector('[data-pdp-addon-variant]');
      var image = row.querySelector('[data-pdp-addon-image] img');
      var selectedPlaceholder = selectedLabel ? selectedLabel.textContent : '';
      var defaultImage = null;

      /* lazysizes has already filled src/srcset by the time a pill is tapped, so
         the swap writes src directly and drops srcset so it can't win. */
      function setImage(src) {
        if (!image) {
          return;
        }
        if (!defaultImage) {
          var lazySrc = (image.getAttribute('data-src') || '').replace('{width}', '180');
          defaultImage = { src: image.currentSrc || image.src || lazySrc, srcset: image.getAttribute('srcset') };
        }
        if (src) {
          image.removeAttribute('srcset');
          image.src = src;
        } else {
          image.src = defaultImage.src;
          if (defaultImage.srcset) {
            image.setAttribute('srcset', defaultImage.srcset);
          }
        }
      }

      function setBusy(busy) {
        toggle.disabled = busy;
        if (confirm) {
          confirm.disabled = busy || !idInput.value;
        }
      }

      function setPanelOpen(open) {
        if (!options) {
          return;
        }
        options.hidden = !open;
        row.classList.toggle('is-open', open);
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      }

      function selectPill(pill) {
        pills.forEach(function (other) {
          var on = other === pill;
          other.classList.toggle('is-selected', on);
          other.setAttribute('aria-checked', on ? 'true' : 'false');
        });
        idInput.value = pill ? pill.getAttribute('data-variant-id') : '';
        if (price) {
          price.textContent = pill ? pill.getAttribute('data-variant-price') : price.getAttribute('data-from-price');
        }
        if (selectedLabel) {
          selectedLabel.textContent = pill ? pill.getAttribute('data-variant-title') : selectedPlaceholder;
        }
        if (confirm) {
          confirm.disabled = !pill;
        }
        setImage(pill ? pill.getAttribute('data-variant-image') : null);
      }

      function markAdded(added) {
        row.classList.toggle('is-added', added);
        toggle.textContent = toggle.getAttribute(added ? 'data-added-label' : 'data-add-label') || toggle.textContent;
        toggle.setAttribute('aria-pressed', added ? 'true' : 'false');
        if (variantLabel) {
          var pill = options ? options.querySelector('[data-pdp-addon-pill].is-selected') : null;
          variantLabel.textContent = added && pill ? pill.getAttribute('data-variant-title') : '';
          variantLabel.hidden = !(added && pill);
        }
      }

      function add() {
        if (!idInput.value) {
          return;
        }
        setBusy(true);
        postCart(theme.routes.cart_add_url, new URLSearchParams(new FormData(form)).toString())
          .then(function () {
            markAdded(true);
            setPanelOpen(false);
          })
          .catch(function () {
            /* leave the row in its previous state on failure */
          })
          .finally(function () {
            setBusy(false);
          });
      }

      function remove() {
        setBusy(true);
        postCart(theme.routes.cart_change_url, new URLSearchParams({ id: idInput.value, quantity: 0 }).toString())
          .then(function () {
            markAdded(false);
            if (varies) {
              selectPill(null);
            }
          })
          .catch(function () {})
          .finally(function () {
            setBusy(false);
          });
      }

      toggle.addEventListener('click', function () {
        if (row.classList.contains('is-added')) {
          remove();
        } else if (varies) {
          setPanelOpen(options.hidden);
        } else {
          add();
        }
      });

      pills.forEach(function (pill) {
        pill.addEventListener('click', function () {
          selectPill(pill);
        });
      });

      if (confirm) {
        confirm.addEventListener('click', add);
      }

      if (options) {
        options.addEventListener('keydown', function (evt) {
          if (evt.key === 'Escape') {
            setPanelOpen(false);
            toggle.focus();
          }
        });
      }
    });
  }

  /* Format picker (cards/dropdown): both styles drive the same real,
   * hidden <select data-single-option-selector> the theme already wires up
   * (assets/theme.js option-selectors handler) — we only set its value and
   * dispatch a bubbling `change`, so price/image/availability updates and
   * cart submission keep working exactly as they do for the native select.
   */
  function selectFormatValue(root, value) {
    var select = root.querySelector('[data-single-option-selector]');
    if (!select || select.value === value) {
      return;
    }
    select.value = value;
    select.dispatchEvent(new Event('change', { bubbles: true, cancelable: false }));
  }

  function syncFormatUI(root) {
    var select = root.querySelector('[data-single-option-selector]');
    if (!select) {
      return;
    }
    var value = select.value;

    root.querySelectorAll('[data-pdp-format-card]').forEach(function (card) {
      var isSelected = card.getAttribute('data-value') === value;
      card.classList.toggle('is-selected', isSelected);
      card.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    });

    root.querySelectorAll('[data-pdp-format-dd-row]').forEach(function (row) {
      var isSelected = row.getAttribute('data-value') === value;
      row.classList.toggle('is-selected', isSelected);
      row.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      if (isSelected) {
        var label = root.querySelector('[data-pdp-format-dd-label]');
        var price = root.querySelector('[data-pdp-format-dd-price]');
        var rowPrice = row.querySelector('.theme-money');
        var rowName = row.querySelector('[data-pdp-format-dd-row-name]');
        if (label) {
          label.textContent = rowName ? rowName.textContent : value;
        }
        if (price && rowPrice) {
          price.textContent = rowPrice.textContent;
        }
      }
    });
  }

  function closeFormatDropdown(root) {
    var toggle = root.querySelector('[data-pdp-format-dd-toggle]');
    var panel = root.querySelector('[data-pdp-format-dd-panel]');
    if (!toggle || !panel) {
      return;
    }
    panel.hidden = true;
    toggle.setAttribute('aria-expanded', 'false');
  }

  function initFormatPickers() {
    var roots = document.querySelectorAll('[data-pdp-format]');
    if (!roots.length) {
      return;
    }

    roots.forEach(function (root) {
      root.addEventListener('click', function (evt) {
        var card = evt.target.closest('[data-pdp-format-card]');
        if (card) {
          selectFormatValue(root, card.getAttribute('data-value'));
          return;
        }

        var row = evt.target.closest('[data-pdp-format-dd-row]');
        if (row) {
          selectFormatValue(root, row.getAttribute('data-value'));
          closeFormatDropdown(root);
          return;
        }

        var toggle = evt.target.closest('[data-pdp-format-dd-toggle]');
        if (toggle) {
          var panel = root.querySelector('[data-pdp-format-dd-panel]');
          if (panel) {
            var isOpen = !panel.hidden;
            panel.hidden = isOpen;
            toggle.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
          }
        }
      });

      document.addEventListener('click', function (evt) {
        if (!root.contains(evt.target)) {
          closeFormatDropdown(root);
        }
      });

      document.addEventListener('keydown', function (evt) {
        if (evt.key === 'Escape') {
          closeFormatDropdown(root);
        }
      });

      var select = root.querySelector('[data-single-option-selector]');
      if (select) {
        select.addEventListener('change', function () {
          syncFormatUI(root);
        });
      }
    });
  }

  function closeSubscriptionDetails() {
    document.querySelectorAll('[data-pdp-subscribe-details].is-open').forEach(function (details) {
      details.classList.remove('is-open');
      var link = details.querySelector('.pdp-subscribe__details-link');
      if (link) {
        link.setAttribute('aria-expanded', 'false');
      }
    });
  }

  document.addEventListener('click', function (evt) {
    if (!evt.target.closest('[data-pdp-subscribe-details]')) {
      closeSubscriptionDetails();
    }
  });

  document.addEventListener('keydown', function (evt) {
    if (evt.key === 'Escape') {
      closeSubscriptionDetails();
    }
  });

  /* "How often" purchase options: keeps the hidden selling_plan input, the
   * row prices and the headline price in step with the chosen option and
   * variant. Any other selling_plan inputs in the form (a subscription app's
   * own) are set to the same value so the cart never receives two answers.
   */
  function planSequences() {
    var plans = window.RS && window.RS.Config && window.RS.Config.sellingPlansJson;
    if (typeof plans === 'string') {
      try {
        plans = JSON.parse(plans);
      } catch (e) {
        return null;
      }
    }
    if (!Array.isArray(plans)) {
      return null;
    }

    var sequences = {};
    plans.forEach(function (plan) {
      if (plan && plan.id != null && plan.frequencySequence != null) {
        sequences[String(plan.id).replace('gid://shopify/SellingPlan/', '')] = plan.frequencySequence;
      }
    });
    return sequences;
  }

  function sortPlans(root) {
    var sequences = planSequences();
    if (!sequences) {
      return;
    }

    root.querySelectorAll('[data-pdp-subscribe-option]').forEach(function (option) {
      var container = option.querySelector('.pdp-subscribe__plans');
      if (!container) {
        return;
      }

      var labels = Array.prototype.slice.call(container.querySelectorAll('.pdp-subscribe__plan'));
      labels
        .map(function (label, index) {
          var sequence = sequences[label.querySelector('[data-pdp-subscribe-plan]').value];
          return { label: label, index: index, sequence: sequence == null ? Infinity : sequence };
        })
        .sort(function (a, b) {
          return a.sequence - b.sequence || a.index - b.index;
        })
        .forEach(function (entry) {
          container.appendChild(entry.label);
        });

      var mode = option.querySelector('[data-pdp-subscribe-mode]');
      var first = container.querySelector('[data-pdp-subscribe-plan]');
      if (mode && !mode.checked && first) {
        first.checked = true;
      }
    });
  }

  function initSubscriptions() {
    document.querySelectorAll('[data-pdp-subscribe]').forEach(function (root) {
      var form = root.closest('form');
      var dataEl = root.querySelector('[data-pdp-subscribe-data]');
      if (!form || !dataEl) {
        return;
      }

      var variants;
      try {
        variants = JSON.parse(dataEl.textContent);
      } catch (e) {
        return;
      }

      sortPlans(root);

      function formatMoney(cents) {
        return window.theme && theme.Shopify && theme.Shopify.formatMoney
          ? theme.Shopify.formatMoney(cents, theme.money_format)
          : (cents / 100).toFixed(2);
      }

      function selectedPlanId(option) {
        var plan = option.querySelector('[data-pdp-subscribe-plan]:checked');
        return plan ? plan.value : '';
      }

      function sync() {
        var variantInput = form.querySelector('[name="id"]');
        var variant = variantInput ? variants[variantInput.value] : null;
        var chosenPlanId = '';

        root.querySelectorAll('[data-pdp-subscribe-option]').forEach(function (option) {
          var mode = option.querySelector('[data-pdp-subscribe-mode]');
          var frequency = option.querySelector('[data-pdp-subscribe-frequency]');
          var isSelected = mode.checked;
          var planId = selectedPlanId(option);

          option.classList.toggle('is-selected', isSelected);
          if (frequency) {
            frequency.hidden = !isSelected;
          }
          var checkedPlan = null;
          option.querySelectorAll('[data-pdp-subscribe-plan]').forEach(function (plan) {
            plan.closest('label').classList.toggle('is-selected', plan.checked);
            if (plan.checked) {
              checkedPlan = plan;
            }
          });
          if (checkedPlan && checkedPlan.dataset.percent) {
            option.querySelectorAll('[data-pdp-subscribe-percent]').forEach(function (percent) {
              percent.textContent = checkedPlan.dataset.percent + '%';
            });
          }
          if (isSelected) {
            chosenPlanId = planId;
          }

          if (!variant) {
            return;
          }
          var price = option.querySelector('[data-pdp-subscribe-price]');
          var save = option.querySelector('[data-pdp-subscribe-save]');
          var planPrice = planId && variant.plans[planId] != null ? variant.plans[planId] : variant.price;
          if (price) {
            price.innerHTML = formatMoney(planPrice);
          }
          if (save) {
            save.innerHTML = planPrice < variant.price ? 'Save ' + formatMoney(variant.price - planPrice) : '';
          }
        });

        form.querySelectorAll('input[name="selling_plan"]').forEach(function (input) {
          input.value = chosenPlanId;
        });

        var express = form.querySelector('[data-pdp-express]');
        if (express) {
          express.hidden = chosenPlanId !== '';
        }

        var headline = document.querySelector('.pdp-price-area .current-price');
        if (headline && variant) {
          var headlinePrice = chosenPlanId && variant.plans[chosenPlanId] != null ? variant.plans[chosenPlanId] : variant.price;
          headline.innerHTML = formatMoney(headlinePrice);
        }
      }

      root.addEventListener('change', sync);

      /* On touchscreens there is no hover, so a tap on "Subscription details"
         opens the flyout instead of leaving the page; the terms link inside it
         still navigates. Pointer users keep the CSS hover and the link itself. */
      var touchOnly = window.matchMedia('(hover: none)');
      root.querySelectorAll('[data-pdp-subscribe-details]').forEach(function (details) {
        var link = details.querySelector('.pdp-subscribe__details-link');
        if (!link) {
          return;
        }
        link.addEventListener('click', function (evt) {
          if (!touchOnly.matches) {
            return;
          }
          evt.preventDefault();
          var open = !details.classList.contains('is-open');
          closeSubscriptionDetails();
          details.classList.toggle('is-open', open);
          link.setAttribute('aria-expanded', String(open));
        });
      });

      /* The theme announces variant changes as a jQuery event and rewrites
         the headline price in its own handler, so sync after that settles. */
      if (window.jQuery) {
        window.jQuery(document).on('variantChanged', function () {
          window.setTimeout(sync, 0);
        });
      }

      sync();
    });
  }

  /* Mobile gallery dots and arrows, driving the theme's slick slideshow. */
  function initGalleryNav() {
    var nav = document.querySelector('[data-pdp-gallery-nav]');
    var $ = window.jQuery;
    if (!nav || !$) {
      return;
    }

    var $slideshow = $(nav).closest('.main-image').find('.product-slideshow');
    var dots = nav.querySelectorAll('[data-pdp-gallery-dot]');

    function setActive(index) {
      dots.forEach(function (dot, i) {
        dot.classList.toggle('is-active', i === index);
        if (i === index) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });
    }

    $slideshow.on('beforeChange', function (evt, slick, current, next) {
      setActive(next);
    });

    function go(method, arg) {
      if ($slideshow.hasClass('slick-initialized')) {
        $slideshow.slick(method, arg);
      }
    }

    dots.forEach(function (dot, i) {
      dot.addEventListener('click', function () {
        go('slickGoTo', i);
      });
    });
    nav.querySelector('[data-pdp-gallery-prev]').addEventListener('click', function () {
      go('slickPrev');
    });
    nav.querySelector('[data-pdp-gallery-next]').addEventListener('click', function () {
      go('slickNext');
    });
  }

  /* "Show all reviews" opens the full Judge.me widget below the three cards. */
  function initReviews() {
    document.querySelectorAll('[data-pdp-reviews]').forEach(function (root) {
      var toggle = root.querySelector('[data-pdp-reviews-all]');
      var widget = root.querySelector('[data-pdp-reviews-widget]');
      if (!toggle || !widget) {
        return;
      }
      toggle.addEventListener('click', function () {
        widget.hidden = false;
        toggle.setAttribute('aria-expanded', 'true');
        toggle.parentNode.hidden = true;
        widget.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }

  /* Order total: the selected variant's price (or its selling plan price) times
   * the quantity, shown on the add to cart button ("Add to cart — £18.90") and
   * in the sticky bar, whose summary line is kept in step too. The theme
   * rewrites the button's text on every variant change, so the total is
   * re-applied after it; a disabled (sold out / unavailable) button keeps the
   * theme's text.
   */
  function initOrderTotal() {
    var button = document.querySelector('[data-pdp-main-add-to-cart]');
    var form = button ? button.closest('form') : null;
    var productId = form ? form.getAttribute('data-product-id') : null;
    var dataEl = productId ? document.getElementById('ProductJson-' + productId) : null;
    if (!form || !dataEl) {
      return;
    }

    var variants = {};
    var planPrices = {};
    try {
      JSON.parse(dataEl.textContent).variants.forEach(function (variant) {
        variants[variant.id] = variant;
      });
      var planEl = form.querySelector('[data-pdp-subscribe-data]');
      if (planEl) {
        planPrices = JSON.parse(planEl.textContent);
      }
    } catch (e) {
      return;
    }

    var stickyTotal = document.querySelector('[data-pdp-sticky-total]');
    var stickyMeta = document.querySelector('[data-pdp-sticky-meta]');
    var stickyButton = document.querySelector('.pdp-sticky-atc__submit');

    function formatMoney(cents) {
      return window.theme && theme.Shopify && theme.Shopify.formatMoney
        ? theme.Shopify.formatMoney(cents, theme.money_format)
        : '£' + (cents / 100).toFixed(2);
    }

    function formatName(variant) {
      var card = form.querySelector('[data-pdp-format-card].is-selected .pdp-format__card-name');
      if (card) {
        return card.textContent.trim();
      }
      return variant.title === 'Default Title' ? '' : variant.title;
    }

    function purchaseOption(variant, planId, planPrice) {
      if (!form.querySelector('[data-pdp-subscribe]')) {
        return '';
      }
      if (!planId) {
        return 'one-time';
      }
      var plan = form.querySelector('[data-pdp-subscribe-plan][value="' + planId + '"]');
      var label = plan ? plan.closest('label').textContent.trim().toLowerCase() : 'subscription';
      var saving = Math.round((1 - planPrice / variant.price) * 100);
      return saving > 0 ? label + ', ' + saving + '% off' : label;
    }

    function update() {
      var variantInput = form.querySelector('[name="id"]');
      var variant = variantInput ? variants[variantInput.value] : null;
      if (!variant) {
        return;
      }

      var planInput = form.querySelector('[data-pdp-subscribe-input]');
      var planId = planInput ? planInput.value : '';
      var plans = planPrices[variant.id] ? planPrices[variant.id].plans : null;
      var unitPrice = planId && plans && plans[planId] != null ? plans[planId] : variant.price;

      var quantityInput = form.querySelector('[name="quantity"]');
      var quantity = quantityInput ? parseInt(quantityInput.value, 10) : 1;
      if (!(quantity > 0)) {
        quantity = 1;
      }

      var total = formatMoney(unitPrice * quantity);

      if (!button.disabled) {
        button.innerHTML = button.getAttribute('data-add-to-cart-text').trim() + ' — ' + total;
      }
      if (stickyTotal) {
        stickyTotal.innerHTML = total;
      }
      if (stickyButton && !stickyButton.disabled) {
        stickyButton.innerHTML =
          '<span class="pdp-sticky-atc__label-full">' + button.getAttribute('data-add-to-cart-text').trim() + '</span>' +
          '<span class="pdp-sticky-atc__label-short">Add — ' + total + '</span>';
      }
      if (stickyMeta) {
        stickyMeta.textContent = [formatName(variant), purchaseOption(variant, planId, unitPrice), 'qty ' + quantity]
          .filter(Boolean)
          .join(' · ');
      }
    }

    form.addEventListener('change', update);
    form.addEventListener('input', update);

    /* The quantity +/- buttons and variant changes are announced as jQuery
       events, which native listeners don't receive. */
    if (window.jQuery) {
      window.jQuery(form).on('change', '[name="quantity"]', update);
      window.jQuery(document).on('variantChanged', function () {
        window.setTimeout(update, 0);
      });
    }

    update();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
      initAddonToggles();
      initFormatPickers();
      initSubscriptions();
      initOrderTotal();
      initReviews();
      initGalleryNav();
    });
  } else {
    init();
    initAddonToggles();
    initFormatPickers();
    initSubscriptions();
    initOrderTotal();
    initReviews();
    initGalleryNav();
  }
})();
